import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Trash2, Upload, Loader2, Image as ImageIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "nexoriq_admin_token";

export default function CertificationsPanel() {
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [issuer, setIssuer] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/certifications`);
      setCerts(res.data || []);
    } catch {
      toast.error("Failed to load certifications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!title.trim() || !file) {
      toast.error("Title and file are required.");
      return;
    }
    const allowed = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowed.includes(file.type)) {
      toast.error("Only PDF, JPG or PNG files are allowed.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10 MB).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("issuer", issuer.trim());
      fd.append("file", file);
      await axios.post(`${API}/admin/certifications`, fd, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Certificate uploaded.");
      setTitle("");
      setIssuer("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this certificate? This cannot be undone.")) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/admin/certifications/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      setCerts((prev) => prev.filter((c) => c.id !== id));
      toast.success("Certificate deleted.");
    } catch {
      toast.error("Delete failed.");
    } finally {
      setDeleting("");
    }
  };

  const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(b / 1024)} KB`);
  const fmt = (d) => new Date(d).toLocaleDateString(undefined, { dateStyle: "medium" });

  return (
    <div data-testid="admin-certifications-panel">
      {/* Upload form */}
      <form onSubmit={upload} className="border border-nx-navy p-6 md:p-8 mb-10 max-w-2xl" data-testid="cert-upload-form">
        <h3 className="font-cabinet font-black text-xl tracking-tight text-nx-navy mb-6">
          Upload a certificate
        </h3>
        <div className="space-y-5">
          <div>
            <label className="block font-mono text-[0.62rem] uppercase tracking-[0.2em] text-nx-steel2 mb-2">
              Title *
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. ISO 9001:2015 Quality Management System"
              className="w-full border border-nx-navy/40 focus:border-nx-navy bg-transparent px-3 py-2.5 text-sm text-nx-navy outline-none"
              data-testid="cert-title-input"
            />
          </div>
          <div>
            <label className="block font-mono text-[0.62rem] uppercase tracking-[0.2em] text-nx-steel2 mb-2">
              Issuer (optional)
            </label>
            <input
              value={issuer}
              onChange={(e) => setIssuer(e.target.value)}
              placeholder="e.g. TÜV Rheinland"
              className="w-full border border-nx-navy/40 focus:border-nx-navy bg-transparent px-3 py-2.5 text-sm text-nx-navy outline-none"
              data-testid="cert-issuer-input"
            />
          </div>
          <div>
            <label className="block font-mono text-[0.62rem] uppercase tracking-[0.2em] text-nx-steel2 mb-2">
              File * (PDF, JPG or PNG — max 10 MB)
            </label>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-nx-navy2 file:mr-3 file:border file:border-nx-navy file:bg-transparent file:px-3 file:py-1.5 file:font-mono file:text-[0.62rem] file:uppercase file:tracking-[0.14em] file:text-nx-navy file:cursor-pointer"
              data-testid="cert-file-input"
            />
          </div>
          <button
            type="submit"
            disabled={uploading}
            className="nx-btn disabled:opacity-60"
            data-testid="cert-upload-submit"
          >
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {uploading ? "Uploading…" : "Upload certificate"}
          </button>
        </div>
      </form>

      {/* List */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-cabinet font-black text-xl tracking-tight text-nx-navy">
          Published certificates
        </h3>
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-steel2">
          {certs.length} record{certs.length === 1 ? "" : "s"}
        </div>
      </div>

      {loading ? (
        <div className="font-mono text-sm uppercase tracking-[0.2em] text-nx-steel2" data-testid="cert-admin-loading">
          → Loading…
        </div>
      ) : certs.length === 0 ? (
        <div className="border border-nx-navy p-6 text-nx-navy2" data-testid="cert-admin-empty">
          No certificates yet. Uploads will appear on the public /certifications page immediately.
        </div>
      ) : (
        <div className="border border-nx-navy" data-testid="cert-admin-table">
          {certs.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-4 px-4 py-4 border-b border-nx-navy/15 last:border-b-0 hover:bg-nx-bg2 transition-colors"
              data-testid={`cert-admin-row-${c.id}`}
            >
              <div className="text-nx-steel2 shrink-0">
                {c.content_type?.startsWith("image/") ? <ImageIcon size={20} /> : <FileText size={20} />}
              </div>
              <div className="min-w-0 flex-1">
                <a
                  href={`${API}/certifications/${c.id}/file`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-cabinet font-black text-nx-navy leading-tight hover:underline"
                >
                  {c.title}
                </a>
                <div className="text-xs text-nx-steel2 mt-0.5 truncate">
                  {c.issuer ? `${c.issuer} · ` : ""}{c.filename} · {fmtSize(c.size)} · {fmt(c.created_at)}
                </div>
              </div>
              <button
                onClick={() => remove(c.id)}
                disabled={deleting === c.id}
                className="text-nx-accent hover:text-nx-navy transition-colors disabled:opacity-40 p-2"
                aria-label="Delete certificate"
                data-testid={`cert-delete-${c.id}`}
              >
                {deleting === c.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
